
import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { CheckCircle2, Droplets, Rat, Sparkles, BadgePlus } from "lucide-react";

const WhyBinWashing = () => {
  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <h1 className="text-4xl font-bold text-center mb-6">Why Sign Up for Our Bin Washing Service?</h1>
      
      <p className="text-lg text-center text-muted-foreground mb-12">
        Tired of dealing with grimy, smelly bins that attract pests and ruin the kerb appeal of your home or business? 
        Our innovative bin washing service is here to transform your waste management experience—while prioritising 
        sustainability and hygiene. Here's why you should join the revolution:
      </p>
      
      <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        <Card className="border-t-4 border-t-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center mb-4">
              <Rat className="h-6 w-6 mr-2 text-blue-500" />
              <h2 className="text-xl font-semibold">Eliminate Vermin & Pests</h2>
            </div>
            <p className="text-muted-foreground">
              Dirty bins are a magnet for rats, flies, and fruit flies, which thrive on leftover residue and organic waste. 
              Our deep-cleaning process blasts away bacteria, food particles, and sticky residues, leaving your bins spotless 
              and unappealing to pests. Say goodbye to infestations and hello to peace of mind.
            </p>
          </CardContent>
        </Card>

        <Card className="border-t-4 border-t-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center mb-4">
              <Sparkles className="h-6 w-6 mr-2 text-green-500" />
              <h2 className="text-xl font-semibold">Banish Bad Smells & Stains</h2>
            </div>
            <p className="text-muted-foreground">
              Lingering odours from rotting waste or spilled liquids can make even stepping near your bin unpleasant. 
              Our state-of-the-art high-pressure cleaning system penetrates every corner, neutralising odours at 
              the source and removing stubborn stains. Your bins will smell fresh, not foul.
            </p>
          </CardContent>
        </Card>

        <Card className="border-t-4 border-t-emerald-500">
          <CardContent className="pt-6">
            <div className="flex items-center mb-4">
              <Droplets className="h-6 w-6 mr-2 text-emerald-500" />
              <h2 className="text-xl font-semibold">Eco-Friendly Excellence</h2>
            </div>
            <p className="text-muted-foreground">
              We're committed to protecting the planet as much as your bins. Our cleaning products are 
              <span className="font-bold"> SEPA-compliant</span>, toxin-free, and biodegradable, ensuring no harm to 
              waterways or wildlife. Plus, our cutting-edge trailers recycle <span className="font-bold">90% of the water used</span> through 
              a four-stage filtration system. This minimises water waste and guarantees we leave the lightest possible 
              environmental footprint.
            </p>
          </CardContent>
        </Card>

        <Card className="border-t-4 border-t-purple-500">
          <CardContent className="pt-6">
            <div className="flex items-center mb-4">
              <BadgePlus className="h-6 w-6 mr-2 text-purple-500" />
              <h2 className="text-xl font-semibold">Kerb Appeal, Restored</h2>
            </div>
            <p className="text-muted-foreground">
              Untidy bins covered in grime or food waste can detract from your property's appearance. Our service 
              leaves bins gleaming like new, enhancing the cleanliness and professionalism of your space—whether 
              it's a home, restaurant, or office.
            </p>
          </CardContent>
        </Card>

        <Card className="border-t-4 border-t-amber-500">
          <CardContent className="pt-6">
            <div className="flex items-center mb-4">
              <CheckCircle2 className="h-6 w-6 mr-2 text-amber-500" />
              <h2 className="text-xl font-semibold">Convenience & Hygiene</h2>
            </div>
            <p className="text-muted-foreground">
              Regular cleaning stops harmful bacteria like <em>E. coli</em> and <em>Salmonella</em> from building up, 
              safeguarding your family, staff, or customers from health risks. With flexible scheduling, we handle 
              the dirty work so you don't have to.
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="mt-12 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
        <h2 className="text-2xl font-bold mb-4">Join the Movement Today!</h2>
        <p className="text-lg mb-6">
          By signing up, you're not just maintaining cleaner bins—you're investing in a healthier environment, 
          a greener planet, and a fresher daily life. Let's wash away the mess, sustainably.
        </p>
        <p className="text-xl font-medium italic mb-8">
          ✨ Your bins deserve better. So does the Earth. ✨
        </p>
        <div className="flex justify-center gap-4">
          <Button size="lg" asChild>
            <Link to="/subscriptions">Sign Up Now</Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <Link to="/pricing">View Pricing</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WhyBinWashing;
